import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import eventApi from '../EventGallery/eventApi';
import { GroupingCancelledError, runOrganiserUploadBatches } from '../EventGallery/EventPage';
import { downloadAndStageDriveFiles, rebuildDownloadedDriveFiles } from '../../utils/backgroundUpload/driveDownloadStage';
import { PRIVACY_POLICY_VERSION } from '../../config/appConfig';

// Renders inside the headless WebView FolderSyncForegroundService spins up to
// resume an incomplete upload job (`kind=upload-job` - see that Kotlin
// class's own doc comment) while the visible app is fully backgrounded/killed.
// Folder auto-sync (`kind=folder-sync`) used to also wake this same headless
// mechanism to run the JS auto-sync engine directly (FolderSyncWorker, since
// removed) - that path has been fully migrated onto the same native
// WorkManager pipeline organiser auto-sync already used (see AutoSyncTrigger.kt
// and useLocalAutoSync.js's nativeTargetMeta), so there is only one kind of
// headless resume left. Hosts the real, unmodified JS pipeline against the
// same-origin IndexedDB/localStorage the visible app already populated, so a
// background run never reimplements any upload/compression logic natively.
// Nothing here is ever meant to be seen.
export default function SyncWorkerPage() {
  return <UploadJobWorker />;
}

// Reads/writes the SAME upload_jobs/<jobId>/ manifest+staged-files directory
// FolderSyncForegroundService's HeadlessSyncBridge (Kotlin) and
// stagingProvider.js (visible-app side, via @capacitor/filesystem) share -
// this headless WebView has no Capacitor Bridge at all (a background Service
// has no Activity to host one), so it talks to window.cmHeadlessSyncBridge
// directly instead of going through stagingProvider.js/nativeUploadService.js
// (both of which require the Bridge and would silently no-op here).
function headlessBridge() {
  return typeof window !== 'undefined' ? window.cmHeadlessSyncBridge : undefined;
}

async function loadHeadlessStagedFile(jobId, entry) {
  const meta = JSON.parse(headlessBridge().readStagedFile(jobId, entry.index));
  if (meta.error) throw new Error(meta.error);
  const res = await fetch(`data:${entry.mimeType || 'image/jpeg'};base64,${meta.base64}`);
  const blob = await res.blob();
  return new File([blob], entry.relativePath, { type: entry.mimeType || blob.type });
}

// Resumes one of the local-file upload flows (organiser initial/add, attendee
// submit, organiser submit, upload-link manual) from wherever a job's
// manifest says it got to. Re-runs the exact same upload-engine function the visible app would
// have called; every remaining (staged-but-not-yet-uploaded) file is
// re-compressed by that function exactly as if it were a fresh upload - a
// mildly wasteful second compression pass over already-compressed bytes, but
// the simplest way to reuse the engines completely unmodified rather than
// threading a "skip compression" flag through all four of them, and there's
// no duplicate-upload risk since only not-yet-confirmed files are resumed.
function UploadJobWorker() {
  const [params] = useSearchParams();
  const jobId = params.get('jobId') || '';
  const flowKind = params.get('flowKind') || '';
  const eventIdParam = params.get('eventId') || '';

  useEffect(() => {
    (async () => {
      const bridge = headlessBridge();
      if (!bridge || !jobId) return;
      let outcome = 'failed';
      // Same notification-triggered cancellation as resumeUploadJob.js (the
      // visible-app-WebView twin of this headless page) - see
      // HeadlessSyncBridge.isJobCancelled's own doc comment for why this uses
      // a plain synchronous bridge call instead of nativeUploadService's
      // async plugin round-trip (no Capacitor Bridge exists in this WebView at
      // all). `cancelledRef`/`abortRef` are the exact same shape
      // runOrganiserUploadBatches/downloadAndStageDriveFiles already expect
      // from a live component's own Cancel button, so every existing
      // checkpoint now also fires for a notification-triggered cancel.
      const cancelledRef = { current: false };
      const abortRef = { current: null };
      const cancelPollId = setInterval(() => {
        if (!cancelledRef.current && bridge.isJobCancelled(jobId)) {
          cancelledRef.current = true;
          abortRef.current?.abort();
        }
      }, 2000);
      try {
        const manifest = JSON.parse(bridge.readManifest(jobId));
        const flowMeta = manifest.flowMeta || {};

        // Two-phase (download-then-upload), handled entirely separately from
        // the four local-file flows below - see driveDownloadStage.js and
        // stagingProvider.js's createDriveJob for the manifest shape
        // (`driveFiles`/`phase`, distinct from `files`/staged/uploaded).
        if (flowKind === 'drive-download-upload') {
          if ((manifest.phase || 'downloading') === 'downloading') {
            await downloadAndStageDriveFiles(jobId, manifest, flowMeta.accessToken, () => {}, () => cancelledRef.current);
            bridge.setPhase(jobId, 'uploading');
          }
          const entries = await rebuildDownloadedDriveFiles(jobId, JSON.parse(bridge.readManifest(jobId)));
          const onDriveFileUploaded = (relativePath) => bridge.markUploaded(jobId, JSON.stringify([relativePath]));
          const event = await eventApi.getEvent(manifest.eventId || eventIdParam);
          // createDriveJob stores exactly one of these, matching which mode
          // started the job (see EventPage.js's runDriveDownloadAndUpload /
          // runDriveDownloadAndUploadAdd) - normalize both into the one
          // payload shape runOrganiserUploadBatches expects regardless of mode.
          const mode = flowMeta.payload ? 'initial' : 'add';
          const payload = flowMeta.payload || {
            source_directory: flowMeta.opts?.sourceDirLabel, source_mode: flowMeta.opts?.sourceModeOverride, limit: 0,
            model: flowMeta.opts?.faceModel, credit_hold_id: flowMeta.opts?.creditHoldId,
            organizer_terms_ack: true, policy_version: PRIVACY_POLICY_VERSION,
          };
          await runOrganiserUploadBatches(event, entries, mode, payload, {
            hooks: { onFileUploaded: onDriveFileUploaded }, uploadCancelledRef: cancelledRef, uploadAbortRef: abortRef,
          });
          outcome = 'success';
          return;
        }

        const pending = (manifest.files || []).filter((f) => f.staged && !f.uploaded);
        const loaded = await Promise.all(pending.map(async (entry) => ({
          file: await loadHeadlessStagedFile(jobId, entry),
          relativePath: entry.relativePath,
        })));
        const onFileUploaded = (relativePath) => bridge.markUploaded(jobId, JSON.stringify([relativePath]));

        // Only attendee-submit/organiser-submit ever reach this headless path
        // now - initial-grouping/add-photos/upload-link-manual moved to the
        // native WorkManager pipeline (CmUploadWorker), which never populates
        // FolderSyncForegroundService's uploadJobs map (the only thing that
        // can wake this worker for kind=upload-job) with those flowKinds
        // anymore. Kept narrow rather than deleted entirely: drive-download-
        // upload (a separate, not-yet-migrated flow) is handled above, before
        // this switch, and attendee-submit/organiser-submit (deliberately not
        // migrated - see the WorkManager rewrite's own plan) still need this
        // exact path. Both post to the same photo-submissions endpoint - the
        // distinct flowKind only keeps job records/logs self-descriptive
        // about who started the upload.
        switch (flowKind) {
          case 'attendee-submit':
          case 'organiser-submit': {
            if (cancelledRef.current) throw new Error('Upload cancelled.');
            const form = new FormData();
            loaded.forEach(({ file }) => form.append('files', file));
            const controller = new AbortController();
            abortRef.current = controller;
            await eventApi.uploadPhotoSubmissions(manifest.eventId || eventIdParam, form, controller.signal, { onFileUploaded });
            break;
          }
          default:
            throw new Error(`Unknown upload flowKind: ${flowKind}`);
        }
        outcome = 'success';
      } catch (err) {
        outcome = (err instanceof GroupingCancelledError || err?.message === 'Upload cancelled.') ? 'cancelled' : 'failed';
      } finally {
        clearInterval(cancelPollId);
        headlessBridge()?.reportJobComplete(jobId, outcome);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, flowKind]);

  return null;
}
